import {
  IBooking,
  IPouchCreds,
  AdminServiceLog,
  IReplicateSubscription,
  IReplicateSubscriptionItem,
} from 'shared-types/index';
import CompareObjectsService, {
  IChangeDetails,
} from 'external-services/compare-objects-service/CompareObjects';
import ObjectCopyService from 'external-services/object-copy-service';
import { keys } from 'ts-transformer-keys';
import {
  InitialReplicateFromProps,
  IInheritedError,
  ReplicationEventType,
  ILiveSync,
  IReplicationDoc,
  IPouchUpdateParams,
  PouchActionsNames
} from './types';
import BookingListFilterService from 'external-services/booking-list-filter-service';
import BookingNormaliseService from "external-services/booking-normalise-service/bookingNormalise.service";
import DateUtilsService from "external-services/date-utils-service";
import BookingLookupService from "external-services/booking-lookup-service";
import {cloneDeep} from "lodash-es";

const NS = 'PouchSyncService';

const logTag = `[Pouch DB - ${NS}] ==>`;

export const quickEditProps: string[] = ['people', 'status'];
const customerProps: string[] = ['firstName', 'lastName', 'email', 'phone', 'company', 'subscribed'];
const bookingKeys: string[] = keys<IBooking>();

class PouchSyncService {
  /**
   * replicateSubscriptions is a complex object, with properties and callback functions, generated
   * by PouchDB. It cannot be serialised and therefore cannot be added to redux store, so we
   * keep a reference to it here. There can only be 1 active instance at a time (1 for each venue).
   */
  private replicateSubscriptions: IReplicateSubscription;

  setReplicateSubscriptions(replicateSubscriptions: IReplicateSubscription) {
    this.replicateSubscriptions = replicateSubscriptions;
  }

  getReplicateSubscription(): IReplicateSubscription {
    return this.replicateSubscriptions;
  }

  getRemoteDBDocsCount(syncLogStack: string[], reducedInfo: IPouchCreds): Promise<number> {
    try {
      console.log(
        logTag,
        `5. Starting Counting remote docs reducedInfo: ${reducedInfo?.url || ''} >>`
      );
      syncLogStack.push(
        `5. Starting Counting remote docs reducedInfo: ${reducedInfo?.url || ''} >>`
      );
    } catch (err) {
      console.error(`5. Starting Counting remote docs -> log ${err}`);
    }

    return this.fetchPouchInfo(reducedInfo)
      .then((info) => {
        try {
          console.log(logTag, `5.1. Finished remote docs counting info: ${info?.db_name || ''} >>`);
          syncLogStack.push(`5.1. Finished remote docs counting info: ${info?.db_name || ''} >>`);
        } catch (err) {
          console.error(`5.1. Finished remote docs counting -> log ${err}`);
        }
        return info.doc_count;
      })
      .catch(() => {
        console.log(logTag, '5.9. Error on remote docs counting  >>');
        syncLogStack.push('5.9. Error on remote docs counting  >>');

        return this.fetchPouchInfo(reducedInfo)
          .then((info: PouchDB.Core.DatabaseInfo) => {
            return info.doc_count;
          })
          .catch(function () {
            return -1;
          });
      });
  }

  public initialReplicateFrom(props: InitialReplicateFromProps) {
    const {
      dbLocal,
      syncLogStack,
      serverLoggingDbEvents,
      // REMOVED replicateSubscriptions and placed it in class variable (call setReplicateSubscriptions first)
      initialReplicaChangeCallback,
      upToDateWithRemoteCallback,
      unauthorisedCallback,
      reducedInfo,
      remoteDBDocsCount,
      isOnlineCreated,
      retryCount,
      inheritedResolve,
      inheritedReject,
      inheritedError,
    } = props;
    const { username, password, url } = reducedInfo;

    if (!window?.config.pouch.initialReplicate || !isOnlineCreated) {
      return inheritedResolve ? inheritedResolve() : Promise.resolve();
    }

    const MAX_RETRIES = 5;

    // Too many retries. Give up and resolve to false.
    if (retryCount >= MAX_RETRIES) {
      let errMessage = '';

      if (inheritedError && ((inheritedError as IInheritedError).status as number) === 401) {
        unauthorisedCallback();
        errMessage = 'unauthorised';
      } else {
        errMessage = inheritedError as string;
      }

      console.error(logTag, '3.1b. Initial Sync failed!!', inheritedError);
      serverLoggingDbEvents(errMessage, AdminServiceLog.WARNLOG, 'sync attempt #' + retryCount);

      inheritedReject();
      return;
    }

    try {
      console.log(
        logTag,
        `8. Start initial replica!!! ${retryCount + 1} - [FROM REMOTE] dbLocal: ${
          dbLocal?.name || ''
        }, reducedInfo: ${reducedInfo?.url || ''} >>`
      );
      syncLogStack.push(
        `8. Start initial replica - [FROM REMOTE] dbLocal: ${dbLocal?.name || ''}, reducedInfo: ${
          reducedInfo?.url || ''
        } >>`
      );
    } catch (err) {
      console.error(`8. Start initial replica -> log ${err}`);
    }

    return new Promise((_resolve, _reject) => {
      const resolve = inheritedResolve || _resolve;
      const reject = inheritedReject || _reject;

      if (!this.replicateSubscriptions) {
        reject(
          `${NS}: you have not set "replicateSubscriptions". "initialReplicateFrom" will not work without this.`
        );
        return;
      }

      const handleError = (err: {}) => {
        console.error(logTag, `3.1a. Intial Sync failed ${retryCount + 1}`, err);

        this.initialReplicateFrom({
          ...props,
          retryCount: props.retryCount + 1,
          inheritedResolve: resolve,
          inheritedReject: reject,
        });
      };

      // use this line to debug the retries
      // if (retryCount < 6) {
      //   handleError('debugging test');
      //   return;
      // }

      this.replicateSubscriptions.initialFrom = (dbLocal.replicate as IReplicateSubscriptionItem)
        .from(url, {
          batch_size: retryCount === 0 ? 2000 : 1000,
          batches_limit: retryCount === 0 ? 100 : 50,
          auth: {
            username,
            password,
          },
          ajax: {
            timeout: 20000,
            cache: true,
          },
        } as any)

        .on('change', async (info: any) => {
          // const remoteDBDocsCount = await this.getRemoteDBDocsCount(syncLogStack, reducedInfo)
          initialReplicaChangeCallback({
            total: remoteDBDocsCount,
            current: info.docs_written,
          });

          console.log(logTag, '8.1. Initial change  >>', info);
          syncLogStack.push('8.1. Initial change  >>');
        })
        .on('complete', (/*info*/) => {
          console.log(logTag, '8.2. Initial Sync completed  >>');
          syncLogStack.push('8.2. Initial Sync completed  >>');

          // Clean subscription
          this.replicateSubscriptions.initialFrom = null;
          // Notify application the DB is ready to be used
          upToDateWithRemoteCallback();
          resolve();
        })
        .on('error', handleError);
    });
  }

  public liveSync(props: ILiveSync) {
    const {
      dbLocal,
      syncLogStack,
      // REMOVED replicateSubscriptions and placed it in class variable (call setReplicateSubscriptions first)
      reducedInfo,
      handleReplicationFrom,
      handleReplicationTo,
    } = props;
    try {
      console.log(
        logTag,
        `10. Starting liveSync dbLocal: ${dbLocal?.name || ''}, reducedInfo: ${
          reducedInfo?.url || ''
        } >>`
      );
      syncLogStack.push(
        `10. Starting liveSync dbLocal: ${dbLocal?.name || ''}, reducedInfo: ${
          reducedInfo?.url || ''
        } >>`
      );
    } catch (err) {
      console.error(`10. Starting liveSync -> log ${err}`);
    }

    if (!this.replicateSubscriptions) {
      throw Error(
        `${NS}: you have not set "replicateSubscriptions". "liveSync" will not work without this.`
      );
      return;
    }

    // Upload local changes into remote DB
    this.replicateSubscriptions.liveTo = (dbLocal.replicate as IReplicateSubscriptionItem)
      .to(reducedInfo.url, {
        filter: (doc: IReplicationDoc) => {
          if (doc._id > 'booking_' && doc._id < 'booking_\uffff' && doc._deleted === true) {
            return false;
          } else if (
            doc._id > 'changes_booking_' &&
            doc._id < 'changes_booking_\uffff' &&
            doc._deleted === true
          ) {
            return false;
          } else if (
            doc._id > 'customer_' &&
            doc._id < 'customer_\uffff' &&
            doc._deleted === true
          ) {
            return false;
          }
          return true;
        },
        live: true,
        retry: true,
        batch_size: 1000,
        batches_limit: 50,
        back_off_function: function () {
          return 1000;
        },
        auth: {
          username: reducedInfo.username,
          password: reducedInfo.password,
        },
        ajax: {
          timeout: 20000,
          cache: true,
        },
      })
      .on('change', (info: any) => {
        handleReplicationTo(ReplicationEventType.change, info);
      })
      .on('paused', (err: any) => {
        if (!err) {
          handleReplicationTo(ReplicationEventType.uptodate);
        } else {
          // handleReplicationTo("error"); // ??
        }
      })
      .on('error', (err: any) => {
        handleReplicationTo(ReplicationEventType.error, err);
      })
      .on('complete', () => {
        console.log('on complete live to');
        this.replicateSubscriptions.liveTo = null;
      });
    // Download remote changes into local DB
    this.replicateSubscriptions.liveFrom = (dbLocal.replicate as IReplicateSubscriptionItem)
      .from(reducedInfo.url, {
        live: true,
        retry: true,
        batch_size: 1000,
        batches_limit: 50,
        back_off_function: function () {
          return 1000;
        },
        auth: {
          username: reducedInfo.username,
          password: reducedInfo.password,
        },
        ajax: {
          timeout: 20000,
          cache: true,
        },
      } as any)
      .on('change', (info: any) => {
        handleReplicationFrom(ReplicationEventType.change, info);
      })
      .on('paused', (err: any) => {
        if (!err) {
          handleReplicationFrom(ReplicationEventType.uptodate);
        } else {
          // handleReplicationFrom(ReplicationEventType.error);
        }
      })
      .on('error', (err: any) => {
        handleReplicationFrom(ReplicationEventType.error, err);
      })
      .on('complete', () => {
        this.replicateSubscriptions.liveFrom = null;
      });
  }

  createIndexes(dbLocal: PouchDB.Database): Promise<PouchDB.Find.CreateIndexResponse<{}>[]> {
    const indexes = [
      {
        fields: ['customerId'],
        name: 'customerIdIndexs',
        ddoc: 'customerIdIndexsDesigndoc',
      },
    ];
    return Promise.all(
      indexes.map(function (index) {
        return dbLocal.createIndex({
          index: index,
        });
      })
    );
  }

  containsOnlyQuickChanges(
    originalBooking: IBooking,
    newBooking: IBooking,
    statusChangesOnlyForBookingsColumn = false,
    ignoreCustomerProps = false
  ): boolean {
    if (!originalBooking || !newBooking) return false;

    // don't care about checking 'lastModifiedDate', as this will always be different
    // for 4823, newBooking will have different value for "customersCovid", "preferredSectionId" than originalBooking
    const ignoreList = ['lastModifiedDate', 'customersCovid', "hasVisitedMultipleVenues"]; // for some reason `true` comes back occasionally as a property name

    // gets copies of objects without angularJS and pouch values getting in th way
    const _orig: any = ObjectCopyService.couchAndAngularSafeCopy(originalBooking);
    const _new: any = ObjectCopyService.couchAndAngularSafeCopy(newBooking);

    /**
     * we need to check if status has changed and by name is enough, so we flatten this variable to avoid it returning all
     * the child properties as changes. This has a couchDB _rev property in it, so we make sure we're not comparing that.
     */
    if (_orig) {
      _orig.status = _orig.status.name || null;
      this.createStringsForArraysWithObjects(_orig);
    }

    _new.status = _new.status.name;

    this.createStringsForArraysWithObjects(_new);

    // console.log(NS, calleeNS, '_orig', _orig);
    // console.log(NS, calleeNS, '_new', _new);

    const changedValues: IChangeDetails[] = CompareObjectsService.computeObjectDifference(
      _orig,
      _new,
      '',
      true,
      ignoreList
    );

    /**
     * Seems to return a lot of junk on the runsheet page, so here we remove from
     * changedValues any values that are not part of the interface for IBooking.
     * Doing reverse order so the splice doesn't go out of order.
     */
    for (let i = changedValues.length - 1; i >= 0; i--) {
      const o: IChangeDetails = changedValues[i];
      if (typeof o.property !== 'string' || !bookingKeys.includes(o.property as string)) {
        changedValues.splice(i, 1);
      }
    }

    // console.log(NS, calleeNS, 'changedValues', changedValues);

    // if only status or people have changed, return true
    return (
      changedValues.length > 0 &&
      changedValues.every(({ property }) => {
        // https://nowbookit.atlassian.net/browse/NBI-8685 - ignore customer props for quick edit for runsheet
        const isQuickEdit = ignoreCustomerProps 
          ? [...quickEditProps, ...customerProps].includes(property as string) 
          : quickEditProps.includes(property as string);
        

        /**
         * Here we only accept status changes that stay in the "bookings" column (which does not include things like
         * Seated, Finished, Cancelled, Wait List or Standby List).
         */
        if (statusChangesOnlyForBookingsColumn && property === 'status') {
          const isOriginalBookingInBookingsColumn =
            BookingListFilterService.isBookingInBookingsColumn(originalBooking);
          const isNewBookingInBookingsColumn =
            BookingListFilterService.isBookingInBookingsColumn(newBooking);

          return isOriginalBookingInBookingsColumn && isNewBookingInBookingsColumn;
        }

        return isQuickEdit;
      })
    );
  }

  private createStringsForArraysWithObjects(obj: any): void {
    for (const prop in obj) {
      if (Array.isArray(obj[prop])) {
        obj[prop] = obj[prop].map((o: any) =>
          JSON.stringify(ObjectCopyService.couchAndAngularSafeCopy(0))
        );
      }
    }
  }

  // only public for unit tests
  fetchPouchInfo(reducedInfo: IPouchCreds): Promise<PouchDB.Core.DatabaseInfo> {
    const { username, password, url } = reducedInfo;
    const remoteDB = new PouchDB(url, {
      auth: {
        username,
        password,
      },
    });

    return remoteDB.info();
  }

  async updateOrInjectDocumentFromPouchUpdate({
    existingBookings,
    documentIds,
    selectedDate,
    actionValueCallback,
    blockoutsCallback
  }: IPouchUpdateParams): Promise<{
    updatedBookingsList: IBooking[],
    recentChangeBookings: IBooking[],
    isNewSameDayBooking?: boolean
  }> {
    const isSingleSyncMatch = documentIds.length === 1; // && payload.docIds[0] === scope.info._id;

    if (isSingleSyncMatch) {
      const updatedBookingId = documentIds[0];
      const actionNameSuffix = this.getCouchDocIdPrefix(updatedBookingId);

      if (actionValueCallback) {
        actionValueCallback({
          actionName: this.getTagActionNameFromId(updatedBookingId),
          value: cloneDeep(documentIds),
        })
      }

      if (actionNameSuffix !== 'booking') {
        // only care about bookings and blockouts
        if (blockoutsCallback && actionNameSuffix === 'blockout') {
          blockoutsCallback();
        }
        return Promise.resolve(null);
      }


      const existingBookingIndex = existingBookings.findIndex(
        (b) => b._id === updatedBookingId
      );

      /**
       * If it is a new booking, adds it to the list
       */
      let booking: IBooking;
      try {
        booking = await BookingLookupService.getById(
          updatedBookingId,
          selectedDate
        );
      } catch (err: any) {
        if (err?.status === 404) { // booking not found, booking time is different with selected date
          if (existingBookingIndex > -1) {// if it's an existing booking, remove it
            const _existingBookings = [...existingBookings];
            _existingBookings.splice(existingBookingIndex, 1);
            return Promise.resolve({
              updatedBookingsList: _existingBookings,
              recentChangeBookings: []
            });
          } else { // means it's a new booking, so we don't need to do anything
            return Promise.resolve(null);
          }
        } else {
          return Promise.reject(err);
        }
      }

      if (existingBookingIndex > -1) {

        const _existingBookings = [...existingBookings];
        if (DateUtilsService.isOnSameDate(booking.time, selectedDate)) {
          _existingBookings.splice(existingBookingIndex, 1, booking);
        } else {
          _existingBookings.splice(existingBookingIndex);
        }

        return Promise.resolve({
          updatedBookingsList: _existingBookings,
          recentChangeBookings: [booking]
        });
      } else {

        // only update if you are currently on the same day
        if (
          booking.type === 'booking' &&
          DateUtilsService.isOnSameDate(booking.time, selectedDate)
        ) {
          // console.log(NS, 'newBooking', newBooking);
          return Promise.resolve({
            updatedBookingsList: [...existingBookings, booking],
            recentChangeBookings: [booking],
            isNewSameDayBooking: true
          });
        }

        return Promise.resolve(null);
      }

    } else {
      // @todo: what happens if multiple bookings are updated? Is this even possible?

      return Promise.resolve(null);
    }
  }


  getTagActionNameFromId(id: string): PouchActionsNames {
    const actionNameSuffix = id.split('_')[0];
    let actionName: PouchActionsNames;
    switch (actionNameSuffix) {
      case 'tag':
        actionName = PouchActionsNames.POUCH_SYNCED_DOCTYPE_TAG;
        break;

      case 'event':
        actionName = PouchActionsNames.POUCH_SYNCED_DOCTYPE_EVENT;
        break;

      // will never be in an id
      // case PouchDocType.receiveChange:
      //   actionName = PouchActionsNames.RECEIVE_CHANGE;
      //   break;

      case 'booking':
        actionName = PouchActionsNames.POUCH_SYNCED_DOCTYPE_BOOKING;
        break;

      case 'layout':
        actionName = PouchActionsNames.POUCH_SYNCED_DOCTYPE_LAYOUT;
        break;

      case 'status':
        actionName = PouchActionsNames.POUCH_SYNCED_DOCTYPE_STATUS;
        break;

      case 'note':
        actionName = PouchActionsNames.POUCH_SYNCED_DOCTYPE_NOTE;
        break;

      case 'blockout':
        actionName = PouchActionsNames.POUCH_SYNCED_DOCTYPE_BLOCKOUT;
        break;

      case 'schedule':
        actionName = PouchActionsNames.POUCH_SYNCED_DOCTYPE_SCHEDULE;
        break;

      case 'section':
        actionName = PouchActionsNames.POUCH_SYNCED_DOCTYPE_SECTION;
        break;

      case 'table':
        actionName = PouchActionsNames.POUCH_SYNCED_DOCTYPE_TABLE;
        break;

      case 'section-group':
        actionName = PouchActionsNames.POUCH_SYNCED_DOCTYPE_SECTION_GROUP;
        break;

      case 'service-group':
        actionName = PouchActionsNames.POUCH_SYNCED_DOCTYPE_SERVICE_GROUP;
        break;
    }

    return actionName;
  }

  getCouchDocIdPrefix(docId: string): string {
    return docId.split('_')[0]
  }

  /**
   * Utility function to check what kind of document was passed with PouchActions.handlePouchSyncedDoctype / PouchDocType.receiveChange
   * @param doc - Couch/Pouch Document received via PouchActions.handlePouchSyncedDoctype
   * @param prefix - Identifying prefix on the doc id (eg "blockout", "booking", "event", etc)
   */
  checkDocIdPrefix(doc: any, prefix: string): boolean {
    return doc[0]._id.startsWith(prefix);
  }
}

const instance = new PouchSyncService();
export default instance as PouchSyncService;
